import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { toast } from "../../stores/toast";
import { Button, Input } from "../ui";
import { ProductImage } from "../ProductCard";
import { ListingTitle } from "../ListingTitle";
import { rm } from "../../lib/format";
import { useSuppressMobileBottomNav } from "../../hooks/useSuppressMobileBottomNav";

interface TradeUser {
  id: string;
  displayName: string;
  emailMasked: string;
}

interface BrowseItem {
  id: string;
  name: string;
  image: string | null;
  value: number;
}

const EXPIRY_OPTIONS = [
  { hours: 1, label: "1 hour" },
  { hours: 6, label: "6 hours" },
  { hours: 24, label: "24 hours" },
  { hours: 72, label: "3 days" },
] as const;

interface TradeWizardProps {
  offerItemIds: string[];
  creditsEnabled: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function TradeWizard({ offerItemIds, creditsEnabled, onClose, onCreated }: TradeWizardProps) {
  useSuppressMobileBottomNav(true);
  const queryClient = useQueryClient();
  const [step, setStep] = useState<"partner" | "request" | "review">("partner");
  const [userSearch, setUserSearch] = useState("");
  const [partner, setPartner] = useState<TradeUser | null>(null);
  const [itemSearch, setItemSearch] = useState("");
  const [requestIds, setRequestIds] = useState<Set<string>>(new Set());
  const [offerCredits, setOfferCredits] = useState("");
  const [requestCredits, setRequestCredits] = useState("");
  const [expiresInHours, setExpiresInHours] = useState<number>(24);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: users, isFetching: usersLoading } = useQuery({
    queryKey: ["trade-users", userSearch],
    queryFn: () => api<{ items: TradeUser[] }>(`/trades/users?search=${encodeURIComponent(userSearch)}`),
    enabled: userSearch.trim().length >= 2,
  });

  const { data: browse } = useQuery({
    queryKey: ["trade-browse", partner?.id, itemSearch],
    queryFn: () => {
      const params = new URLSearchParams({ userId: partner!.id });
      if (itemSearch.trim()) params.set("search", itemSearch.trim());
      return api<{ items: BrowseItem[] }>(`/trades/browse?${params}`);
    },
    enabled: !!partner && step !== "partner",
  });

  useEffect(() => {
    setRequestIds(new Set());
    setItemSearch("");
  }, [partner?.id]);

  async function createTrade() {
    if (!partner) return;
    if (requestIds.size === 0 && !(Number(requestCredits) > 0)) {
      toast("error", "Request at least one item or credits from your partner");
      return;
    }
    setBusy(true);
    try {
      await api("/trades", {
        body: {
          toUserId: partner.id,
          offerItemIds,
          requestItemIds: [...requestIds],
          offerCredits: Number(offerCredits) || 0,
          requestCredits: Number(requestCredits) || 0,
          expiresInHours,
          message: message.trim() || undefined,
        },
      });
      await queryClient.invalidateQueries({ queryKey: ["trades"] });
      await queryClient.invalidateQueries({ queryKey: ["vault"] });
      toast("success", "Trade offer sent");
      onCreated();
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't create trade");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm">
      {step === "partner" ? (
        <div className="flex h-full items-center justify-center p-4">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-brand-dark p-4 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <p className="font-display text-sm font-semibold">Find trading partner</p>
              <button type="button" onClick={onClose} className="rounded-full p-1 text-white/50 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <p className="mb-4 text-center text-[11px] text-white/45">
              Search by name or email to start a trade with {offerItemIds.length} item
              {offerItemIds.length > 1 ? "s" : ""}.
            </p>

            <div className="space-y-3">
              <Input
                placeholder="Search name or email"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
              {usersLoading && <p className="text-center text-[11px] text-white/40">Searching…</p>}
              <div className="max-h-64 space-y-1.5 overflow-y-auto">
                {users?.items.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => {
                      setPartner(u);
                      setStep("request");
                    }}
                    className="flex w-full items-center gap-2 rounded-xl border border-white/5 bg-surface p-3 text-left text-xs hover:border-brand-pink/40"
                  >
                    <Search size={14} className="text-white/30" />
                    <div>
                      <p className="font-semibold">{u.displayName}</p>
                      <p className="text-white/45">{u.emailMasked}</p>
                    </div>
                  </button>
                ))}
                {userSearch.length >= 2 && !usersLoading && !users?.items.length && (
                  <p className="py-4 text-center text-[11px] text-white/40">No users found</p>
                )}
                {userSearch.length > 0 && userSearch.length < 2 && (
                  <p className="py-2 text-center text-[11px] text-white/40">Type at least 2 characters</p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col justify-end">
          <div className="max-h-[85vh] overflow-y-auto rounded-t-3xl border border-white/10 bg-brand-dark p-4 pb-8">
            <div className="mb-3 flex items-center justify-between">
              <p className="font-display text-sm font-semibold">New trade</p>
              <button type="button" onClick={onClose} className="rounded-full p-1 text-white/50 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <p className="mb-3 text-[11px] text-white/45">
              Offering {offerItemIds.length} item{offerItemIds.length > 1 ? "s" : ""} · cards lock until trade completes or is cancelled
            </p>

        {step === "request" && partner && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs">
              <p>
                Trading with <span className="font-semibold text-brand-pink">{partner.displayName}</span>
              </p>
              <button type="button" onClick={() => setStep("partner")} className="text-brand-pink">
                Change
              </button>
            </div>
            <Input
              placeholder="Search their vault"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
            />
            <div className="grid max-h-48 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
              {browse?.items.map((item) => {
                const checked = requestIds.has(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      const next = new Set(requestIds);
                      if (checked) next.delete(item.id);
                      else next.add(item.id);
                      setRequestIds(next);
                    }}
                    className={`overflow-hidden rounded-xl border text-left ${
                      checked ? "border-brand-pink bg-brand-pink/10" : "border-white/5 bg-surface"
                    }`}
                  >
                    <ProductImage src={item.image} alt="" className="aspect-square w-full" />
                    <div className="p-1.5">
                      <ListingTitle className="text-[10px] leading-[14px] min-h-[14px]">{item.name}</ListingTitle>
                      <p className="text-[9px] text-white/45">{rm(item.value)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            {creditsEnabled && (
              <div className="grid grid-cols-2 gap-2">
                <Input
                  label="You add credits"
                  inputMode="numeric"
                  placeholder="0"
                  value={offerCredits}
                  onChange={(e) => setOfferCredits(e.target.value)}
                />
                <Input
                  label="You request credits"
                  inputMode="numeric"
                  placeholder="0"
                  value={requestCredits}
                  onChange={(e) => setRequestCredits(e.target.value)}
                />
              </div>
            )}
            <Button className="w-full" onClick={() => setStep("review")} disabled={requestIds.size === 0 && !(Number(requestCredits) > 0)}>
              Review trade
            </Button>
          </div>
        )}

        {step === "review" && partner && (
          <div className="space-y-3">
            <div className="rounded-xl bg-white/5 p-3 text-[11px]">
              <p className="text-white/45">Partner</p>
              <p className="font-semibold">{partner.displayName}</p>
              <p className="mt-2 text-white/45">You give · {offerItemIds.length} item(s)</p>
              {Number(offerCredits) > 0 && <p className="text-brand-pink">+ {offerCredits} credits</p>}
              <p className="mt-2 text-white/45">You request · {requestIds.size} item(s)</p>
              {Number(requestCredits) > 0 && <p className="text-brand-pink">+ {requestCredits} credits</p>}
            </div>
            <div>
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40">Expires in</p>
              <div className="grid grid-cols-4 gap-1.5">
                {EXPIRY_OPTIONS.map((opt) => (
                  <button
                    key={opt.hours}
                    type="button"
                    onClick={() => setExpiresInHours(opt.hours)}
                    className={`rounded-lg border py-1.5 text-[10px] font-semibold ${
                      expiresInHours === opt.hours ? "border-brand-pink bg-brand-pink/10 text-brand-pink" : "border-white/10 text-white/60"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <Input
              placeholder="Message (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => setStep("request")}>
                Back
              </Button>
              <Button disabled={busy} onClick={createTrade}>
                Send offer
              </Button>
            </div>
          </div>
        )}
          </div>
        </div>
      )}
    </div>
  );
}
